import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { 
  Phone, Mail, MapPin, Star, Clock, Users, Plane, Hotel, 
  Calendar, ChevronRight, Menu, X, Check, CreditCard,
  Sun, Cloud, Wind, Droplets, Shield, Award, Globe, Gem, Trophy, Ticket
} from "lucide-react";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import OldUrlRedirect from "./pages/OldUrlRedirect";
import VoucherPage from "./pages/VoucherPage";
import AdminVouchers from "./pages/AdminVouchers";

// Official Payment Brand Logos Component - All inline SVGs for reliability
const PaymentLogos = ({ size = "md" }) => {
  const h = size === "sm" ? "h-6" : size === "lg" ? "h-10" : "h-7";
  return (
    <div className="flex items-center gap-3" data-testid="payment-logos">
      {/* Visa */}
      <div className="bg-white rounded px-2 py-1 flex items-center justify-center" title="Visa accepted for Hollywood tour bookings">
        <svg viewBox="0 0 780 500" className={`${h} w-auto`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Visa payment accepted for Hollywood tours">
          <path d="M293.2 348.7l33.4-195.7h53.4l-33.4 195.7zM540.7 157.3c-10.6-4-27.2-8.3-47.9-8.3-52.8 0-90 26.6-90.2 64.7-.3 28.2 26.5 43.9 46.8 53.3 20.8 9.6 27.8 15.8 27.7 24.4-.1 13.2-16.6 19.2-32 19.2-21.4 0-32.7-3-50.3-10.2l-6.9-3.1-7.5 44c12.5 5.5 35.6 10.2 59.6 10.5 56.1 0 92.5-26.3 92.9-67 .2-22.3-14-39.3-44.8-53.3-18.7-9.1-30.1-15.1-30-24.3 0-8.1 9.7-16.8 30.6-16.8 17.4-.3 30.1 3.5 39.9 7.5l4.8 2.3 7.3-42.9zM676.3 153h-41.3c-12.8 0-22.4 3.5-28 16.3l-79.4 179.4h56.1s9.2-24.1 11.2-29.4c6.1 0 60.6.1 68.4.1 1.6 6.9 6.5 29.3 6.5 29.3h49.6l-43.1-195.7zm-65.8 126.4c4.4-11.3 21.3-54.7 21.3-54.7-.3.5 4.4-11.3 7.1-18.7l3.6 16.9s10.2 46.8 12.4 56.5h-44.4zM247.8 153l-52.3 133.5-5.6-27.1c-9.7-31.3-39.9-65.2-73.7-82.2l47.8 171.2 56.5-.1 84.1-195.4h-56.8z" fill="#1a1f71"/>
          <path d="M146.9 153H60.1l-.7 4c67 16.2 111.4 55.4 129.7 102.5l-18.7-89.8c-3.2-12.4-12.6-16.2-23.5-16.7z" fill="#f7a600"/>
        </svg>
      </div>
      {/* Mastercard */}
      <div className="bg-white rounded px-2 py-1 flex items-center justify-center" title="Mastercard accepted for Hollywood tour bookings">
        <svg viewBox="0 0 152 100" className={`${h} w-auto`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Mastercard payment accepted for Hollywood tours">
          <rect width="152" height="100" rx="8" fill="white"/>
          <circle cx="60" cy="50" r="30" fill="#EB001B"/>
          <circle cx="92" cy="50" r="30" fill="#F79E1B"/>
          <path d="M76 27.5a29.9 29.9 0 0 0-11.2 22.5A29.9 29.9 0 0 0 76 72.5a29.9 29.9 0 0 0 11.2-22.5A29.9 29.9 0 0 0 76 27.5z" fill="#FF5F00"/>
        </svg>
      </div>
      {/* American Express */}
      <div className="bg-white rounded px-2 py-1 flex items-center justify-center" title="American Express accepted for Hollywood tour bookings">
        <svg viewBox="0 0 152 100" className={`${h} w-auto`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="American Express payment accepted for Hollywood tours">
          <rect width="152" height="100" rx="8" fill="#006FCF"/>
          <text x="76" y="42" textAnchor="middle" fill="white" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="18">AMERICAN</text>
          <text x="76" y="64" textAnchor="middle" fill="white" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="18">EXPRESS</text>
        </svg>
      </div>
      {/* Discover */}
      <div className="bg-white rounded px-2 py-1 flex items-center justify-center" title="Discover accepted for Hollywood tour bookings">
        <svg viewBox="0 0 152 100" className={`${h} w-auto`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Discover payment accepted for Hollywood tours">
          <rect width="152" height="100" rx="8" fill="white" stroke="#ddd" strokeWidth="1"/>
          <text x="76" y="56" textAnchor="middle" fill="#FF6600" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="22">DISCOVER</text>
          <circle cx="115" cy="50" r="18" fill="#FF6600" opacity="0.3"/>
        </svg>
      </div>
      {/* PayPal */}
      <div className="bg-white rounded px-2 py-1 flex items-center justify-center" title="PayPal accepted for Hollywood tour bookings">
        <svg viewBox="0 0 152 100" className={`${h} w-auto`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="PayPal payment accepted for Hollywood tours">
          <rect width="152" height="100" rx="8" fill="white" stroke="#ddd" strokeWidth="1"/>
          <text x="46" y="58" textAnchor="middle" fill="#003087" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="22" fontStyle="italic">Pay</text>
          <text x="100" y="58" textAnchor="middle" fill="#009CDE" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="22" fontStyle="italic">Pal</text>
        </svg>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component
const Logo = () => (
  <Link to="/" className="flex items-center gap-3">
    <img 
      src="/images/official-hollywood-tours-logo.png" 
      alt="Official Hollywood Tours LLC - #1 Rated Hollywood Sightseeing Tour Company" 
      className="h-12 w-auto"
      width="160"
      height="60"
      decoding="async"
      fetchPriority="high"
    />
    <div className="hidden sm:block">
      <span className="font-accent text-xl tracking-wider text-[#C9A227]">OFFICIAL</span>
      <span className="block text-sm font-semibold text-[#1a1a1a]">Hollywood Tours <span className="text-[#B22234]">LLC</span></span>
    </div>
  </Link>
);

// Weather Widget Component
const WeatherWidget = () => {
  const [weather, setWeather] = useState({
    temp: 72,
    condition: "Sunny",
    humidity: 45,
    wind: 8
  });

  return (
    <div className="weather-card" data-testid="weather-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm opacity-80">Hollywood, CA</p>
          <p className="text-3xl font-bold">{weather.temp}°F</p>
        </div>
        <Sun className="w-12 h-12 text-yellow-300" />
      </div>
      <div className="flex justify-between text-sm opacity-80">
        <span className="flex items-center gap-1"><Droplets className="w-4 h-4" /> {weather.humidity}%</span>
        <span className="flex items-center gap-1"><Wind className="w-4 h-4" /> {weather.wind} mph</span>
      </div>
      <p className="text-center mt-2 text-sm font-medium">{weather.condition} - Perfect for touring!</p>
    </div>
  );
};

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Tours", href: "#tours" },
    { name: "Custom Travel", href: "#custom-travel" },
    { name: "Vegas 2 Hollywood", href: "#vegas" },
    { name: "Book Flights", href: "#flights" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-lg" : "bg-white"}`} data-testid="navbar">
      <div className="container-custom">
        {/* Top bar with contact */}
        <div className="hidden md:flex items-center justify-between py-2 border-b border-gray-100 text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+18448015604" className="flex items-center gap-2 text-gray-600 hover:text-[#C9A227] transition-colors">
              <Phone className="w-4 h-4" /> +1 844 801 5604
            </a>
            <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-2 text-gray-600 hover:text-[#C9A227] transition-colors">
              <Mail className="w-4 h-4" /> info@officialhollywoodtours.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="iata-badge px-3 py-1 rounded text-xs font-bold flex items-center gap-1">
              <img src="/images/iata-accredited-travel-agency-logo.png" alt="IATA Accredited Hollywood Travel Agency" className="h-5 w-auto" width="26" height="20" decoding="async" loading="lazy" /> IATA ACCREDITED
            </div>
            <span className="text-2xl">🇺🇸</span>
          </div>
        </div>

        {/* Main nav */}
        <nav className="flex items-center justify-between py-4">
          <Logo />
          
          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link text-gray-700 hover:text-[#C9A227] font-medium transition-colors"
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a href="#book" className="btn-gold px-6 py-3 rounded-full text-sm" data-testid="book-now-btn">
              Book Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t"
            >
              <div className="py-4 space-y-3">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-[#C9A227]"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </a>
                ))}
                <a href="#book" className="block mx-4 btn-gold text-center py-3 rounded-full">
                  Book Now
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-32 overflow-hidden" data-testid="hero-section">
      {/* Background image - LA cityscape */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 hero-bg bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?auto=format&fit=crop&w=2000&q=80')" }}
          role="img"
          aria-label="Hollywood sightseeing tours - Los Angeles skyline cityscape at sunset"
        />
        {/* Stronger gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
      </div>

      {/* USA Flag accent - top right */}
      <div className="absolute top-24 right-4 z-5 hidden lg:block">
        <div className="relative">
          <img 
            src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg" 
            alt="Hollywood Tours in the United States - Book American Sightseeing Experiences" 
            className="w-28 h-auto shadow-lg rounded"
            style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
            width="112"
            height="59"
            decoding="async"
            loading="lazy"
          />
          <div className="absolute -bottom-2 -left-2 bg-[#C9A227] text-black text-xs font-bold px-2 py-1 rounded">
            IATA Accredited
          </div>
        </div>
      </div>

      {/* Hollywood Stars decoration */}
      <div className="absolute bottom-20 left-10 z-5 hidden xl:block opacity-20">
        <Star className="w-16 h-16 text-[#C9A227] fill-[#C9A227]" />
      </div>
      <div className="absolute top-40 right-1/4 z-5 hidden xl:block opacity-15">
        <Star className="w-12 h-12 text-[#C9A227] fill-[#C9A227]" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Deal Banner */}
            <div className="inline-flex items-center gap-2 bg-[#B22234] text-white px-4 py-2 rounded-full mb-6 deal-badge shadow-lg">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-bold text-sm">LIMITED OFFER: Save 15% on Group Bookings!</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-[#1a1a1a]">
              #1 Rated{" "}
              <span className="text-gold-gradient">Hollywood</span>{" "}
              <span className="text-[#B22234]">Sightseeing Tours</span>
              <span className="inline-block ml-3 text-3xl">🇺🇸</span>
            </h1>
            
            {/* Improved text readability with background */}
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl mb-6 shadow-sm">
              <p className="text-lg text-[#1a1a1a] leading-relaxed font-medium">
                <span className="font-bold text-[#C9A227]">Official Hollywood Tours LLC</span> — the #1 rated Hollywood sightseeing tour company 
                in Los Angeles. IATA Accredited since 2009. From the Walk of Fame to celebrity homes in Beverly Hills, 
                our sightseeing tours include lunch, expert guides, and unforgettable memories.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
              <a href="#tours" className="btn-gold px-8 py-4 rounded-full flex items-center gap-2 shadow-lg" data-testid="explore-tours-btn">
                Explore Tours <ChevronRight className="w-5 h-5" />
              </a>
              <a href="#custom-travel" className="btn-outline-gold px-8 py-4 rounded-full shadow-md">
                Custom Travel
              </a>
            </div>

            {/* Trust badges with better background */}
            <div className="flex flex-wrap items-center gap-6 bg-white/70 backdrop-blur-sm px-4 py-3 rounded-xl w-fit">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm text-[#1a1a1a] font-semibold">4.9/5 (340,928+ Reviews)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#1a1a1a] font-semibold">
                <Users className="w-5 h-5 text-[#C9A227]" />
                50,000+ Happy Tourists
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <WeatherWidget />
            
            {/* Quick booking card */}
            <div className="mt-6 card-clean p-6 shadow-xl">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C9A227]" /> Quick Search
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Your Tour</label>
                  <select className="w-full input-light p-3 rounded-lg border-2 border-gray-200 focus:border-[#C9A227] transition-all" data-testid="tour-type-select">
                    <option>Select Tour Type</option>
                    <option>Hollywood Fame Tour - $285</option>
                    <option>Official Hollywood Tour - $255</option>
                    <option>Hollywood to Disneyland - $135</option>
                    <option>Vegas to Hollywood - $215</option>
                  </select>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1 text-[#C9A227]" />
                    Choose Your Date
                  </label>
                  <input 
                    type="date" 
                    className="w-full input-light p-3 rounded-lg border-2 border-gray-200 focus:border-[#C9A227] transition-all cursor-pointer date-picker-animated" 
                    data-testid="tour-date-input"
                    placeholder="Select date"
                  />
                  <div className="absolute right-3 top-9 pointer-events-none">
                    <Calendar className="w-5 h-5 text-[#C9A227] animate-pulse" />
                  </div>
                </div>
                <button className="w-full btn-red py-4 rounded-lg text-lg font-bold shadow-lg hover:shadow-xl transition-all" data-testid="check-availability-btn">
                  Check Availability
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* USA decorative element */}
      <div className="absolute bottom-0 left-0 right-0 h-2 usa-stripes" />
    </section>
  );
};

// FIFA World Cup 2026 Banner
const FifaBanner = () => {
  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    navigator.clipboard.writeText("WORLDCUP2026");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="fifa2026" className="relative overflow-hidden" data-testid="fifa-banner">
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1723079426787-c54d04669613?auto=format&fit=crop&w=2000&q=80')" }}
          role="img"
          aria-label="FIFA World Cup 2026 Los Angeles stadium - Hollywood tours for soccer fans visiting LA"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0a2e]/95 via-[#0d1b3e]/90 to-[#1a0a2e]/85" />
        {/* Animated soccer ball pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      </div>

      <div className="container-custom relative z-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* FIFA Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9A227] to-[#e6c84a] text-black px-5 py-2 rounded-full mb-6 shadow-lg">
              <Trophy className="w-5 h-5" />
              <span className="font-black text-sm tracking-wider">FIFA WORLD CUP 2026</span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
              The World Cup<br />
              Comes to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C9A227] via-[#f0d860] to-[#C9A227]">Los Angeles!</span>
            </h2>
            
            <p className="text-white/80 text-lg mb-6 max-w-lg leading-relaxed">
              FIFA World Cup 2026 is here — and <strong className="text-white">LA is center stage</strong>. 
              Combine the world's biggest sporting event with Hollywood's most iconic sightseeing tours. 
              Explore the Walk of Fame, celebrity homes, and the magic of Tinseltown while you're in town for the games!
            </p>

            {/* Discount Code */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-8 max-w-md">
              <p className="text-[#C9A227] font-bold text-sm mb-2 flex items-center gap-2">
                <Ticket className="w-4 h-4" /> EXCLUSIVE FIFA VISITOR DISCOUNT
              </p>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-white/15 border-2 border-dashed border-[#C9A227] rounded-lg px-5 py-3 flex-1">
                  <span className="font-mono font-black text-2xl text-white tracking-widest" data-testid="fifa-discount-code">WORLDCUP2026</span>
                </div>
                <button 
                  onClick={copyCode}
                  className="bg-[#C9A227] hover:bg-[#b8912a] text-black font-bold px-4 py-3 rounded-lg transition-all active:scale-95"
                  data-testid="copy-code-btn"
                >
                  {copied ? <Check className="w-5 h-5" /> : "Copy"}
                </button>
              </div>
              <p className="text-white/70 text-sm">
                <strong className="text-white">10% OFF</strong> all group tours — valid for FIFA World Cup visitors through 2026!
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="#tours" className="bg-gradient-to-r from-[#C9A227] to-[#e6c84a] text-black font-black px-8 py-4 rounded-full flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all hover:scale-105" data-testid="fifa-explore-tours-btn">
                Explore Tours <ChevronRight className="w-5 h-5" />
              </a>
              <a href="#custom-travel" className="border-2 border-white/40 text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-all flex items-center gap-2">
                <Globe className="w-5 h-5" /> Plan Custom Trip
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Soccer themed card */}
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#C9A227] to-[#8B7500] mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="m12 2 3.5 5.5L22 9l-4 4.5 1.5 6.5-7.5-3-7.5 3L6 13.5 2 9l6.5-1.5z"/>
                    </svg>
                  </div>
                  <h3 className="text-white font-black text-2xl mb-2">LA Match Day + Hollywood Tour</h3>
                  <p className="text-white/60 text-sm">The ultimate FIFA experience package</p>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    "Hollywood Sightseeing Tour",
                    "Walk of Fame & Celebrity Homes",
                    "Beverly Hills & Rodeo Drive",
                    "Lunch at Exclusive Restaurant",
                    "Hotel Pickup & Drop-off",
                    "Custom Game Day Itinerary"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-white/90">
                      <div className="w-6 h-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#C9A227]" />
                      </div>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#B22234] to-[#d63447] rounded-xl p-4 text-center">
                  <p className="text-white/80 text-xs mb-1">Group Tours Starting At</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-white/50 text-lg line-through">$255</span>
                    <span className="text-white font-black text-3xl">$230</span>
                  </div>
                  <p className="text-white/70 text-xs mt-1">with code WORLDCUP2026</p>
                </div>
              </div>

              {/* Floating flags */}
              <div className="absolute -top-4 -right-4 bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
                <span className="text-2xl">🇺🇸</span>
                <span className="text-2xl">🇲🇽</span>
                <span className="text-2xl">🇨🇦</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Tours Section
const ToursSection = () => {
  const tours = [
    {
      id: "hollywood-fame",
      name: "Hollywood Fame Tour",
      tagline: "Walk of Fame & Celebrity Experience",
      description: "Explore the glamour of Hollywood with our signature sightseeing tour featuring the iconic Walk of Fame, TCL Chinese Theatre, and exclusive shopping stops.",
      duration: "1 Day",
      price: 285,
      originalPrice: 325,
      image: "https://images.pexels.com/photos/4700140/pexels-photo-4700140.jpeg?auto=compress&cs=tinysrgb&w=800",
      imageAlt: "Hollywood Walk of Fame sightseeing tour - Celebrity star sidewalk experience in Los Angeles",
      highlights: ["Walk of Fame Stars", "TCL Chinese Theatre", "Celebrity Hotspots", "Shopping Time"],
      isBestseller: true,
      includesLunch: true,
      isPremium: false
    },
    {
      id: "official-hollywood",
      name: "Official Hollywood Tour",
      tagline: "The Complete California Experience",
      description: "An all-inclusive sightseeing tour from Hollywood exploring the best of California. See iconic landmarks, celebrity homes, and breathtaking views.",
      duration: "1 Day",
      price: 255,
      originalPrice: 299,
      image: "https://images.unsplash.com/photo-1563641676291-9f01de32c82d?auto=format&fit=crop&w=800&q=80",
      imageAlt: "Official Hollywood sightseeing tour - Beverly Hills celebrity homes and Hollywood Sign views",
      highlights: ["Hollywood Sign Views", "Beverly Hills", "Celebrity Homes", "Sunset Boulevard"],
      isBestseller: false,
      includesLunch: true,
      isPremium: false
    },
    {
      id: "disneyland",
      name: "Hollywood to Disneyland",
      tagline: "The Magical Family Adventure",
      description: "Discover the iconic Hollywood Sign then head to the happiest place on Earth! Perfect for families with convenient hotel pickup.",
      duration: "1 Day",
      price: 135,
      originalPrice: 165,
      image: "https://images.unsplash.com/photo-1597466599360-3b9775841aec?auto=format&fit=crop&w=800&q=80",
      imageAlt: "Hollywood to Disneyland family tour - Theme park adventure with hotel pickup in Los Angeles",
      highlights: ["Hotel Pickup", "Hollywood Sign Photo Op", "Disneyland Entry", "Return Transfer"],
      isBestseller: false,
      includesLunch: false,
      isPremium: false
    }
  ];

  return (
    <section id="tours" className="section-padding bg-gray-50" data-testid="tours-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="font-accent text-[#C9A227] text-lg tracking-wider">HOLLYWOOD SIGHTSEEING TOURS</span>
          <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4">
            Unforgettable <span className="text-gold-gradient">Sightseeing</span> Experiences
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose from our curated selection of premium Hollywood sightseeing tours. Each tour includes 
            expert local guides, comfortable air-conditioned transportation, lunch, and memories that last a lifetime.
          </p>
        </motion.div>

        {/* Tours Grid - 2x2 Layout */}
        <div className="grid md:grid-cols-2 gap-8">
          {tours.map((tour, index) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="tour-card group"
              data-testid={`tour-card-${tour.id}`}
            >
              {/* Deal ribbon */}
              <div className="ribbon">Save ${tour.originalPrice - tour.price}</div>
              
              <div className="flex flex-col lg:flex-row">
                <div className="relative overflow-hidden lg:w-2/5 h-56 lg:h-auto">
                  <img 
                    src={tour.image} 
                    alt={tour.imageAlt || `${tour.name} - Hollywood sightseeing tour in Los Angeles`}
                    className="w-full h-full object-cover img-zoom"
                    width="400"
                    height="300"
                    decoding="async"
                    loading="lazy"
                  />
                  {tour.isBestseller && (
                    <div className="absolute top-4 left-4 badge-gold px-3 py-1 rounded-full text-sm">
                      BESTSELLER
                    </div>
                  )}
                  {tour.includesLunch && (
                    <div className="absolute bottom-4 left-4 bg-white/90 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      🍽️ Lunch Included
                    </div>
                  )}
                </div>

                <div className="p-6 lg:w-3/5 flex flex-col justify-between">
                  <div>
                    <p className="text-[#C9A227] text-sm font-semibold mb-1">{tour.tagline}</p>
                    <h3 className="text-2xl font-bold mb-2">{tour.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{tour.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {tour.highlights.slice(0, 4).map((h, i) => (
                        <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-gray-400 line-through text-sm">${tour.originalPrice}</span>
                        <span className="text-3xl font-bold text-[#B22234] ml-2">${tour.price}</span>
                        <span className="text-gray-500 text-sm">/person</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <Clock className="w-4 h-4" /> {tour.duration}
                      </div>
                    </div>

                    <a 
                      href="#book" 
                      className="btn-gold w-full text-center py-3 rounded-lg block"
                      data-testid={`book-tour-${tour.id}`}
                    >
                      Book This Tour
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Premium Music Studio Section
const MusicStudioSection = () => {
  return (
    <section id="music-studio" className="section-padding bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] text-white relative overflow-hidden" data-testid="music-studio-section">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M30 0L30 60M0 30L60 30\" stroke=\"%23C9A227\" stroke-width=\"1\"/%3E%3C/svg%3E')"}} />
      </div>
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#C9A227] text-black px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 fill-black" />
              <span className="font-bold text-sm">EXCLUSIVE VIP EXPERIENCE</span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6">
              Music Studio <span className="text-[#C9A227]">Tour & Session</span>
            </h2>
            
            <p className="text-white/80 mb-6 text-lg">
              An exclusive, once-in-a-lifetime opportunity to step inside a professional Hollywood recording studio 
              and work alongside <span className="text-[#C9A227] font-bold">Grammy-connected producers</span> and 
              top music industry executives in the heart of the entertainment capital.
            </p>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl mb-8">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#C9A227]" /> What's Included
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Professional Studio Tour",
                  "Meet Top Music Executives",
                  "Music Industry Networking",
                  "Recording Session Time",
                  "Executive Feedback Session",
                  "Professional Consultation",
                  "Lunch with the Team",
                  "Photo Opportunities"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#C9A227]" />
                <span className="font-semibold">12 Hour Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C9A227]" />
                <span className="font-semibold">Limited Availability</span>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-6">
              <div>
                <p className="text-white/60 text-sm mb-1">Custom Package Starting At</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-[#C9A227]">$3,500</span>
                </div>
              </div>
              <a 
                href="#custom-travel" 
                className="btn-gold px-8 py-4 rounded-full flex items-center gap-2"
                data-testid="book-music-studio-btn"
              >
                Request This Experience <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1627928173650-fd1e0f2c24fc?auto=format&fit=crop&w=800&q=80"
                alt="Hollywood music studio recording session tour - Professional VIP studio experience in Los Angeles"
                className="w-full h-[500px] object-cover object-center"
                width="800"
                height="500"
                decoding="async"
                loading="lazy"
              />
              {/* Logo overlay on studio image */}
              <div className="absolute top-4 left-4 z-10">
                <img 
                  src="/images/official-hollywood-tours-logo.png" 
                  alt="Official Hollywood Tours - #1 Rated Sightseeing Tour Company Logo" 
                  className="h-16 w-auto drop-shadow-lg"
                  width="160"
                  height="60"
                  decoding="async"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-16 h-16 rounded-full border-2 border-[#C9A227] bg-gradient-to-br from-[#C9A227] to-[#8B7500] flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
                  </div>
                  <div>
                    <p className="font-bold text-lg text-white">Hollywood's Finest Producers</p>
                    <p className="text-white/70 text-sm">Grammy-Connected Music Executives</p>
                    <p className="text-[#C9A227] text-xs">The Heart of the Music Industry</p>
                  </div>
                </div>
                <p className="text-white/80 text-sm italic">
                  "Step into the studio where hits are made. This is your chance to experience the magic of Hollywood's music scene with real industry heavyweights."
                </p>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-[#B22234] text-white px-6 py-3 rounded-full shadow-lg transform rotate-12">
              <span className="font-accent text-lg tracking-wider">VIP ACCESS</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Custom Travel Section with $85 Fee
const CustomTravelSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    travelType: "",
    groupSize: 2,
    departureCity: "",
    destination: "",
    departureDate: "",
    returnDate: "",
    flightClass: "",
    hotelStars: "",
    themes: [],
    specialRequests: "",
    budget: ""
  });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const themes = [
    "Celebrity Homes", "Movie Studios", "Beach & Coastal", "Nightlife & Entertainment",
    "Theme Parks", "Shopping", "Food & Wine", "Historical Sites", "Nature & Scenic", "Music Studio Experience"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/custom-tour-request`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        group_size: formData.groupSize,
        preferred_date: formData.departureDate,
        themes: formData.themes,
        special_requests: `Travel Type: ${formData.travelType}, From: ${formData.departureCity}, To: ${formData.destination}, Return: ${formData.returnDate}, Flight Class: ${formData.flightClass}, Hotel: ${formData.hotelStars}, Budget: ${formData.budget}. Notes: ${formData.specialRequests}`,
        budget_range: formData.budget
      });
      toast.success("Request submitted! Our team will contact you within 24 hours.");
      setStep(4);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="custom-travel" className="section-padding" data-testid="custom-travel-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-accent text-[#C9A227] text-lg tracking-wider">CUSTOM ARRANGEMENTS</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6">
              Your Dream Trip,{" "}
              <span className="text-[#B22234]">Your Way</span>
            </h2>
            <p className="text-gray-600 mb-8">
              Let our IATA-accredited travel experts craft the perfect Hollywood experience for you. 
              From flights and luxury hotels to custom tour itineraries, we handle every detail.
            </p>

            <div className="space-y-4 mb-8">
              {[
                "Custom Tour Itineraries Based on Your Theme",
                "Flight Bookings to Any US Destination",
                "Luxury Hotel & Resort Arrangements",
                "Complete Vacation Packages",
                "Group & Corporate Travel Planning",
                "VIP Concierge Services"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#C9A227]" />
                  </div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            {/* $85 Travel Arrangement Fee Notice */}
            <div className="bg-gradient-to-r from-[#002868] to-[#3C3B6E] text-white p-6 rounded-xl" data-testid="travel-fee-notice">
              <div className="flex items-start gap-4">
                <CreditCard className="w-8 h-8 text-[#C9A227] flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-lg mb-2">Travel Arrangement Service Fee</h4>
                  <p className="text-sm opacity-90 mb-4">
                    A <span className="text-[#C9A227] font-bold text-xl">$85</span> non-refundable service fee is required to begin 
                    processing custom travel arrangements. This fee covers our expert consultation, 
                    itinerary planning, and booking services.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs opacity-75">Secure Payment:</span>
                    <PaymentLogos size="sm" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="card-clean p-8"
            data-testid="custom-travel-form"
          >
            {/* Progress steps - 2 steps for Music Studio, 3 for others */}
            <div className="flex items-center justify-between mb-8">
              {(formData.travelType === 'music-studio' ? [1, 3] : [1, 2, 3]).map((s, idx, arr) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-[#C9A227] text-black' : 'bg-gray-200 text-gray-500'}`}>
                    {step > s ? <Check className="w-5 h-5" /> : idx + 1}
                  </div>
                  {idx < arr.length - 1 && <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#C9A227]' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>

            {step === 4 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Request Submitted!</h3>
                <p className="text-gray-600">Our travel experts will contact you within 24 hours to discuss your custom arrangements.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Step 1: Contact Info */}
                {step === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold mb-4">Contact Information</h3>
                    <input
                      type="text"
                      placeholder="Full Name *"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      data-testid="custom-name-input"
                    />
                    <input
                      type="email"
                      placeholder="Email Address *"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      data-testid="custom-email-input"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number *"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      data-testid="custom-phone-input"
                    />
                    <select
                      value={formData.travelType}
                      onChange={(e) => setFormData({...formData, travelType: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      required
                      data-testid="travel-type-select"
                    >
                      <option value="">What are you looking for? *</option>
                      <option value="custom-tour">Custom Sightseeing Tour</option>
                      <option value="music-studio">Music Studio Experience ($3,500+)</option>
                      <option value="flights">Flight Booking Only</option>
                      <option value="hotel">Hotel Booking Only</option>
                      <option value="vacation-package">Complete Vacation Package</option>
                      <option value="group-travel">Group/Corporate Travel</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setStep(formData.travelType === 'music-studio' ? 3 : 2)}
                      className="w-full btn-gold py-3 rounded-lg"
                      disabled={!formData.name || !formData.email || !formData.phone || !formData.travelType}
                    >
                      Continue <ChevronRight className="inline w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Step 2: Trip Details - Skip for Music Studio */}
                {step === 2 && formData.travelType !== 'music-studio' && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold mb-4">Trip Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Departing From"
                        value={formData.departureCity}
                        onChange={(e) => setFormData({...formData, departureCity: e.target.value})}
                        className="w-full input-light p-3 rounded-lg"
                        data-testid="departure-city-input"
                      />
                      <input
                        type="text"
                        placeholder="Destination"
                        value={formData.destination}
                        onChange={(e) => setFormData({...formData, destination: e.target.value})}
                        className="w-full input-light p-3 rounded-lg"
                        data-testid="destination-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Departure Date</label>
                        <input
                          type="date"
                          value={formData.departureDate}
                          onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
                          className="w-full input-light p-3 rounded-lg"
                          data-testid="departure-date-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Return Date</label>
                        <input
                          type="date"
                          value={formData.returnDate}
                          onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                          className="w-full input-light p-3 rounded-lg"
                          data-testid="return-date-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Travelers</label>
                        <input
                          type="number"
                          min="1"
                          value={formData.groupSize}
                          onChange={(e) => setFormData({...formData, groupSize: parseInt(e.target.value)})}
                          className="w-full input-light p-3 rounded-lg"
                          data-testid="group-size-input"
                        />
                      </div>
                      <select
                        value={formData.flightClass}
                        onChange={(e) => setFormData({...formData, flightClass: e.target.value})}
                        className="w-full input-light p-3 rounded-lg mt-6"
                        data-testid="flight-class-select"
                      >
                        <option value="">Flight Class</option>
                        <option value="economy">Economy</option>
                        <option value="premium">Premium Economy</option>
                        <option value="business">Business</option>
                        <option value="first">First Class</option>
                      </select>
                    </div>
                    <select
                      value={formData.hotelStars}
                      onChange={(e) => setFormData({...formData, hotelStars: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      data-testid="hotel-stars-select"
                    >
                      <option value="">Hotel Preference</option>
                      <option value="3-star">3-Star (Budget Friendly)</option>
                      <option value="4-star">4-Star (Comfortable)</option>
                      <option value="5-star">5-Star (Luxury)</option>
                      <option value="resort">Resort/Boutique Hotel</option>
                    </select>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 btn-outline-gold py-3 rounded-lg"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep(3)}
                        className="flex-1 btn-gold py-3 rounded-lg"
                      >
                        Continue <ChevronRight className="inline w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Preferences */}
                {step === 3 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold mb-4">
                      {formData.travelType === 'music-studio' ? 'Studio Session Details' : 'Your Preferences'}
                    </h3>
                    {formData.travelType !== 'music-studio' && (
                      <p className="text-sm text-gray-500 mb-2">Select tour themes you're interested in:</p>
                    )}
                    {formData.travelType === 'music-studio' ? (
                      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2d2d2d] text-white p-6 rounded-xl mb-4">
                        <h4 className="font-bold text-[#C9A227] mb-3">Music Studio Experience - $3,500+</h4>
                        <p className="text-sm text-white/80 mb-4">
                          A full 12-hour day with Grammy-connected music producers and industry executives. 
                          Includes studio tour, recording session time, professional consultation, and lunch.
                        </p>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="text-sm text-white/60 mb-1 block">Preferred Date *</label>
                            <input
                              type="date"
                              value={formData.departureDate}
                              onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
                              className="w-full bg-white/10 border border-white/20 text-white p-3 rounded-lg"
                              required
                              data-testid="studio-date-input"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-white/60 mb-1 block">Group Size</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={formData.groupSize}
                              onChange={(e) => setFormData({...formData, groupSize: parseInt(e.target.value)})}
                              className="w-full bg-white/10 border border-white/20 text-white p-3 rounded-lg"
                              data-testid="studio-group-input"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {themes.map((theme) => (
                          <button
                            key={theme}
                            type="button"
                            onClick={() => {
                              const newThemes = formData.themes.includes(theme)
                                ? formData.themes.filter(t => t !== theme)
                                : [...formData.themes, theme];
                              setFormData({...formData, themes: newThemes});
                            }}
                            className={`px-3 py-2 rounded-full text-sm border transition-colors ${
                              formData.themes.includes(theme)
                                ? 'bg-[#C9A227] text-black border-[#C9A227]'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-[#C9A227]'
                            }`}
                            data-testid={`theme-${theme.toLowerCase().replace(/ /g, '-')}`}
                          >
                            {theme}
                          </button>
                        ))}
                      </div>
                    )}
                    <select
                      value={formData.budget}
                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                      className="w-full input-light p-3 rounded-lg"
                      data-testid="budget-select"
                    >
                      <option value="">Approximate Budget Per Person</option>
                      <option value="under-500">Under $500</option>
                      <option value="500-1000">$500 - $1,000</option>
                      <option value="1000-2500">$1,000 - $2,500</option>
                      <option value="2500-5000">$2,500 - $5,000</option>
                      <option value="5000+">$5,000+</option>
                    </select>
                    <textarea
                      placeholder="Any special requests or notes for our team..."
                      value={formData.specialRequests}
                      onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                      className="w-full input-light p-3 rounded-lg h-24 resize-none"
                      data-testid="special-requests-textarea"
                    />

                    {/* Fee reminder */}
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> {formData.travelType === 'music-studio' 
                          ? 'The Music Studio Experience starts at $3,500. Our team will contact you to discuss details and arrange payment.'
                          : 'A $85 service fee will be collected to begin processing your custom travel request. Our team will contact you to complete payment.'}
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(formData.travelType === 'music-studio' ? 1 : 2)}
                        className="flex-1 btn-outline-gold py-3 rounded-lg"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="flex-1 btn-red py-3 rounded-lg flex items-center justify-center gap-2"
                        disabled={isSubmitting}
                        data-testid="submit-custom-request"
                      >
                        {isSubmitting ? "Submitting..." : "Submit Request"}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Vegas 2 Hollywood Section
const VegasSection = () => {
  return (
    <section id="vegas" className="section-padding bg-gradient-to-br from-[#002868] to-[#3C3B6E] text-white relative overflow-hidden" data-testid="vegas-section">
      {/* Stars overlay */}
      <div className="absolute inset-0 stars-overlay opacity-20" />
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-accent text-[#C9A227] text-lg tracking-wider">FROM VEGAS TO HOLLYWOOD</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6">
              The <span className="text-[#C9A227]">Vegas 2 Hollywood</span> Experience
            </h2>
            <p className="text-white/80 mb-6">
              Established in 2009 and powered by Official Hollywood Tours LLC, the Vegas 2 Hollywood tour 
              is the original and most trusted day trip from Las Vegas to Hollywood, California.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: Clock, text: "Full Day Tour" },
                { icon: Users, text: "Small Groups" },
                { icon: Award, text: "Since 2009" },
                { icon: Shield, text: "IATA Accredited" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/10 p-3 rounded-lg">
                  <item.icon className="w-5 h-5 text-[#C9A227]" />
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/10 p-6 rounded-xl mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-2xl font-bold">Vegas to Hollywood Tour</span>
                <div>
                  <span className="line-through text-white/50">$255</span>
                  <span className="text-3xl font-bold text-[#C9A227] ml-2">$215</span>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C9A227]" /> Hotel pickup from Las Vegas Strip</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C9A227]" /> Breakfast included</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C9A227]" /> Lunch at beach city restaurant</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C9A227]" /> Hollywood Sign close-up views</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C9A227]" /> Return by 6:30 PM for nightlife</li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="https://vegas2hollywood.com" target="_blank" rel="noopener noreferrer" className="btn-gold px-8 py-3 rounded-full">
                Book Vegas Tour
              </a>
              <a href="tel:+17029415144" className="btn-outline-gold border-white text-white hover:bg-white hover:text-[#002868] px-8 py-3 rounded-full flex items-center gap-2">
                <Phone className="w-4 h-4" /> (702) 941-5144
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?auto=format&fit=crop&w=800&q=80"
              alt="Las Vegas to Hollywood day trip tour - Vegas Strip sightseeing with hotel pickup"
              className="rounded-2xl shadow-2xl"
              width="800"
              height="531"
              decoding="async"
              loading="lazy"
            />
            <div className="absolute -bottom-6 -left-6 bg-white text-black p-4 rounded-xl shadow-lg">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="font-bold">4.9/5</span>
              </div>
              <p className="text-sm text-gray-600">TripAdvisor Rating</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Book Section with OHT Booking Widget
const BookSection = () => {
  const [widgetFailed, setWidgetFailed] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://vegas2hollywood.com/embed/booking-widget.js';
    script.async = true;
    script.setAttribute('data-api', 'https://vegas2hollywood.com/api');
    script.setAttribute('data-brand', 'oht');
    script.setAttribute('data-container', 'oht-booking');
    script.onerror = () => setWidgetFailed(true);
    document.body.appendChild(script);

    const checkTimer = setTimeout(() => {
      const container = document.getElementById('oht-booking');
      if (container && container.innerHTML.trim().length < 50) {
        setWidgetFailed(true);
      }
    }, 12000);

    return () => {
      clearTimeout(checkTimer);
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <section id="book" className="section-padding bg-gray-50" data-testid="book-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="font-accent text-[#C9A227] text-lg tracking-wider">INSTANT CONFIRMATION</span>
          <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4">
            Book Your <span className="text-[#B22234]">Tour</span> Now
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Secure your spot on one of our premium Hollywood tours. Instant confirmation and flexible cancellation policies.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="booking-widget-container" data-testid="booking-widget">
            <div id="oht-booking"></div>
            {widgetFailed && (
              <div className="bg-white border-2 border-[#C9A227] rounded-2xl p-8 text-center shadow-lg" data-testid="widget-fallback-card">
                <Phone className="w-10 h-10 text-[#B22234] mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Our Online Booking Is Taking a Quick Break</h3>
                <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                  No worries — our friendly team is standing by to book your Hollywood tour instantly over the phone or by email.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="tel:+18448015604" className="inline-flex items-center gap-2 bg-[#B22234] text-white px-8 py-3 rounded-full font-bold hover:bg-[#8f1b2a] transition-colors" data-testid="fallback-call-button">
                    <Phone className="w-5 h-5" /> Call +1 844 801 5604
                  </a>
                  <a href="mailto:info@officialhollywoodtours.com" className="inline-flex items-center gap-2 bg-[#002868] text-white px-8 py-3 rounded-full font-bold hover:bg-[#001d4d] transition-colors" data-testid="fallback-email-button">
                    <Mail className="w-5 h-5" /> Email Us
                  </a>
                </div>
                <p className="text-sm text-gray-500 mt-4">Instant confirmation · IATA Accredited · Since 2010</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Flights Section
const FlightsSection = () => {
  return (
    <section id="flights" className="section-padding" data-testid="flights-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80"
              alt="Book flights to Los Angeles for Hollywood tours - IATA accredited travel agency flight booking"
              className="rounded-2xl shadow-xl"
              width="800"
              height="531"
              decoding="async"
              loading="lazy"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-accent text-[#C9A227] text-lg tracking-wider flex items-center gap-2">
              <Plane className="w-5 h-5" /> IATA ACCREDITED AGENCY
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6">
              We Book <span className="text-gold-gradient">Flights</span> Too!
            </h2>
            <p className="text-gray-600 mb-6">
              As a full-service IATA accredited travel agency, we can book flights to any destination 
              in the United States. Let us handle your complete travel itinerary from departure to return.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: Globe, title: "Any US Destination", desc: "Domestic flights nationwide" },
                { icon: Shield, title: "IATA Accredited", desc: "Trusted & certified" },
                { icon: CreditCard, title: "Best Rates", desc: "Competitive pricing" },
                { icon: Hotel, title: "Hotel Packages", desc: "Flight + hotel deals" }
              ].map((item, i) => (
                <div key={i} className="card-clean p-4">
                  <item.icon className="w-8 h-8 text-[#C9A227] mb-2" />
                  <h4 className="font-bold">{item.title}</h4>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>

            <a href="#custom-travel" className="btn-navy px-8 py-4 rounded-full inline-flex items-center gap-2">
              Request Flight Quote <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: "",
        inquiry_type: "general",
        message: formData.message
      });
      toast.success("Message sent! We'll respond within 24 hours.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-gray-50" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-accent text-[#C9A227] text-lg tracking-wider">GET IN TOUCH</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6">
              Travel With <span className="text-[#B22234]">Us!</span>
            </h2>
            <p className="text-gray-600 mb-8">
              Have questions? Our team is here to help you plan the perfect Hollywood experience. 
              Reach out and we'll respond within 24 hours.
            </p>

            <div className="space-y-6">
              <a href="tel:+18448015604" className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-[#C9A227]/10 flex items-center justify-center group-hover:bg-[#C9A227] transition-colors">
                  <Phone className="w-6 h-6 text-[#C9A227] group-hover:text-black transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Call Us</p>
                  <p className="font-bold text-lg">+1 844 801 5604</p>
                </div>
              </a>
              <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-[#C9A227]/10 flex items-center justify-center group-hover:bg-[#C9A227] transition-colors">
                  <Mail className="w-6 h-6 text-[#C9A227] group-hover:text-black transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email Us</p>
                  <p className="font-bold text-lg">info@officialhollywoodtours.com</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#C9A227]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-bold text-lg">Hollywood, California 🇺🇸</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <WeatherWidget />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="card-clean p-8" data-testid="contact-form">
              <h3 className="text-xl font-bold mb-6">Send Us a Message</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full input-light p-3 rounded-lg"
                  data-testid="contact-name-input"
                />
                <input
                  type="email"
                  placeholder="Email Address *"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full input-light p-3 rounded-lg"
                  data-testid="contact-email-input"
                />
                <textarea
                  placeholder="Your Message *"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full input-light p-3 rounded-lg resize-none"
                  data-testid="contact-message-textarea"
                />
                <p className="text-sm text-gray-400">{formData.message.length}/500</p>
                <button
                  type="submit"
                  className="w-full btn-gold py-3 rounded-lg"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// SEO Content Section - keyword-rich text for search engine ranking
const SeoContentSection = () => (
  <section className="bg-white py-16 border-t border-gray-100" data-testid="seo-content">
    <div className="container-custom">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1a1a]">
          Hollywood Sightseeing Tours — The #1 Rated Tour Experience in Los Angeles
        </h2>
        <div className="prose prose-lg text-gray-600 space-y-4">
          <p>
            <strong>Official Hollywood Tours LLC</strong> is the premier provider of <strong>Hollywood sightseeing tours</strong> in 
            Los Angeles, California. Since 2009, we have guided over 340,000 tourists from around the world on unforgettable 
            sightseeing tours through Hollywood, Beverly Hills, the Sunset Strip, and beyond. As a fully <strong>IATA accredited 
            travel agency</strong>, we offer a level of trust and professionalism that no other Hollywood tour company can match.
          </p>
          <p>
            Our <strong>Hollywood sightseeing tours</strong> include visits to every iconic landmark: the <strong>Hollywood Walk 
            of Fame</strong>, the legendary <strong>Hollywood Sign</strong>, the <strong>TCL Chinese Theatre</strong>, celebrity homes 
            in <strong>Beverly Hills</strong>, <strong>Rodeo Drive</strong>, <strong>Sunset Boulevard</strong>, and exclusive dining 
            at Hollywood's top restaurants. Every sightseeing tour includes lunch, air-conditioned luxury transportation, and 
            small group sizes for a personalized experience.
          </p>
          <p>
            Whether you're looking for <strong>affordable Hollywood sightseeing tours</strong> starting at just $135, our premium 
            <strong>Hollywood Fame Tour</strong> at $285 with VIP access, or a custom private sightseeing experience, Official 
            Hollywood Tours has the perfect package. We also offer the original <strong>Vegas to Hollywood</strong> day trip 
            ($215) that has been running since 2009, and <strong>Hollywood to Disneyland</strong> adventures for families.
          </p>
          <p>
            What makes our <strong>Hollywood sightseeing tours</strong> the best in Los Angeles? Our guides are born-and-raised 
            locals with insider knowledge you won't find anywhere else. We maintain a <strong>4.9/5 star rating</strong> across 
            over 340,000 reviews. We accept all major credit cards including Visa, Mastercard, American Express, and PayPal. 
            And as an IATA accredited agency, we can also book your flights, hotels, and complete vacation packages.
          </p>
          <p>
            Book your <strong>Hollywood sightseeing tour</strong> today by calling <a href="tel:+18448015604" className="text-[#C9A227] font-semibold">+1 844 801 5604</a> or 
            emailing <a href="mailto:info@officialhollywoodtours.com" className="text-[#C9A227] font-semibold">info@officialhollywoodtours.com</a>. 
            Use code <strong>WORLDCUP2026</strong> for 10% off group sightseeing tours during the FIFA World Cup 2026 in Los Angeles!
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/hollywood-sightseeing-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Hollywood Sightseeing Tours →</Link>
          <Link to="/best-hollywood-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Best Hollywood Tours →</Link>
          <Link to="/celebrity-homes-tour-hollywood" className="text-sm text-[#C9A227] font-semibold hover:underline">Celebrity Homes Tour →</Link>
          <Link to="/hollywood-walk-of-fame-tour" className="text-sm text-[#C9A227] font-semibold hover:underline">Walk of Fame Tour →</Link>
          <Link to="/hollywood-sign-tour" className="text-sm text-[#C9A227] font-semibold hover:underline">Hollywood Sign Tour →</Link>
          <Link to="/beverly-hills-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Beverly Hills Tours →</Link>
          <Link to="/los-angeles-sightseeing-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">LA Sightseeing Tours →</Link>
          <Link to="/affordable-hollywood-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Affordable Tours →</Link>
          <Link to="/private-hollywood-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Private Tours →</Link>
          <Link to="/hollywood-group-tours" className="text-sm text-[#C9A227] font-semibold hover:underline">Group Tours →</Link>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => {
  // International flags for welcoming tourists
  const internationalFlags = [
    { country: "USA", flag: "🇺🇸" },
    { country: "UK", flag: "🇬🇧" },
    { country: "Canada", flag: "🇨🇦" },
    { country: "Germany", flag: "🇩🇪" },
    { country: "France", flag: "🇫🇷" },
    { country: "Spain", flag: "🇪🇸" },
    { country: "Italy", flag: "🇮🇹" },
    { country: "Japan", flag: "🇯🇵" },
    { country: "Australia", flag: "🇦🇺" },
    { country: "Brazil", flag: "🇧🇷" },
    { country: "Mexico", flag: "🇲🇽" },
    { country: "China", flag: "🇨🇳" },
  ];

  return (
    <footer className="bg-[#1a1a1a] text-white" data-testid="footer">
      {/* USA stripe top */}
      <div className="h-2 usa-stripes" />
      
      {/* International Welcome Banner */}
      <div className="bg-gradient-to-r from-[#002868] to-[#3C3B6E] py-4">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="text-white/80 text-sm font-medium">Welcome Tourists From Around The World</span>
            <div className="flex flex-wrap justify-center gap-2">
              {internationalFlags.map((item, i) => (
                <span 
                  key={i} 
                  className="text-2xl hover:scale-125 transition-transform cursor-default" 
                  title={item.country}
                >
                  {item.flag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <img 
              src="/images/official-hollywood-tours-logo.png" 
              alt="Official Hollywood Tours" 
              className="h-16 mb-4"
              width="160"
              height="60"
              decoding="async"
              loading="lazy"
            />
            <p className="text-gray-400 text-sm mb-4">
              Official Hollywood Tours LLC - Your IATA accredited gateway to California's most spectacular 
              sightseeing experiences. Established 2009.
            </p>
            <div className="iata-badge inline-flex items-center gap-2 px-3 py-2 rounded text-sm">
              <img src="/images/iata-accredited-travel-agency-logo.png" alt="IATA Accredited Hollywood Tour Company" className="h-6 w-auto" width="31" height="24" decoding="async" loading="lazy" /> IATA Accredited
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/hollywood-sightseeing-tours" className="hover:text-[#C9A227] transition-colors font-semibold text-[#C9A227]">Hollywood Sightseeing Tours</Link></li>
              <li><a href="#tours" className="hover:text-[#C9A227] transition-colors">Our Tours</a></li>
              <li><a href="#custom-travel" className="hover:text-[#C9A227] transition-colors">Custom Travel</a></li>
              <li><a href="#vegas" className="hover:text-[#C9A227] transition-colors">Vegas 2 Hollywood</a></li>
              <li><a href="#flights" className="hover:text-[#C9A227] transition-colors">Book Flights</a></li>
              <li><a href="#book" className="hover:text-[#C9A227] transition-colors">Book Now</a></li>
              <li><Link to="/hollywood-tours" className="hover:text-[#C9A227] transition-colors">Hollywood Tours</Link></li>
              <li><Link to="/hollywood-vacation-packages" className="hover:text-[#C9A227] transition-colors">Vacation Packages</Link></li>
            </ul>
          </div>

          {/* Tours */}
          <div>
            <h4 className="font-bold text-lg mb-4">Our Tours</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#tours" className="hover:text-[#C9A227] transition-colors">Hollywood Fame Tour</a></li>
              <li><a href="#tours" className="hover:text-[#C9A227] transition-colors">Official Hollywood Tour</a></li>
              <li><a href="#tours" className="hover:text-[#C9A227] transition-colors">Hollywood to Disneyland</a></li>
              <li><a href="https://vegas2hollywood.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A227] transition-colors">Vegas to Hollywood</a></li>
              <li><a href="#music-studio" className="hover:text-[#C9A227] transition-colors">Music Studio Experience</a></li>
              <li><Link to="/celebrity-homes-tour-hollywood" className="hover:text-[#C9A227] transition-colors">Celebrity Homes Tour</Link></li>
              <li><Link to="/beverly-hills-tours" className="hover:text-[#C9A227] transition-colors">Beverly Hills Tours</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C9A227]" />
                <a href="tel:+18448015604" className="hover:text-[#C9A227]">+1 844 801 5604</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#C9A227]" />
                <a href="mailto:info@officialhollywoodtours.com" className="hover:text-[#C9A227] text-sm">info@officialhollywoodtours.com</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C9A227]" />
                Hollywood, CA 🇺🇸
              </li>
            </ul>
            <div className="mt-4">
              <PaymentLogos size="sm" />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          {/* SEO Internal Links */}
          <div className="mb-8">
            <h4 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Popular Hollywood Tours & Travel</h4>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <Link to="/hollywood-sightseeing-tours" className="hover:text-[#C9A227] font-bold text-gray-400">Hollywood Sightseeing Tours</Link>
              <Link to="/hollywood-tours" className="hover:text-[#C9A227]">Hollywood Tours</Link>
              <Link to="/celebrity-homes-tour-hollywood" className="hover:text-[#C9A227]">Celebrity Homes Tour</Link>
              <Link to="/hollywood-walk-of-fame-tour" className="hover:text-[#C9A227]">Walk of Fame Tour</Link>
              <Link to="/hollywood-bus-tours" className="hover:text-[#C9A227]">Hollywood Bus Tours</Link>
              <Link to="/hollywood-sign-tour" className="hover:text-[#C9A227]">Hollywood Sign Tour</Link>
              <Link to="/beverly-hills-tours" className="hover:text-[#C9A227]">Beverly Hills Tours</Link>
              <Link to="/las-vegas-to-hollywood-tour" className="hover:text-[#C9A227]">Vegas to Hollywood</Link>
              <Link to="/hollywood-vacation-packages" className="hover:text-[#C9A227]">Vacation Packages</Link>
              <Link to="/hollywood-vacations" className="hover:text-[#C9A227]">Hollywood Vacations</Link>
              <Link to="/hollywood-tour" className="hover:text-[#C9A227]">Hollywood Tour</Link>
              <Link to="/trips-to-hollywood" className="hover:text-[#C9A227]">Trips to Hollywood</Link>
              <Link to="/hollywood-travel" className="hover:text-[#C9A227]">Hollywood Travel</Link>
              <Link to="/hollywood-city-tours" className="hover:text-[#C9A227]">Hollywood City Tours</Link>
              <Link to="/private-hollywood-tours" className="hover:text-[#C9A227]">Private Tours</Link>
              <Link to="/hollywood-vip-tours" className="hover:text-[#C9A227]">VIP Tours</Link>
              <Link to="/best-hollywood-tours" className="hover:text-[#C9A227]">Best Hollywood Tours</Link>
              <Link to="/affordable-hollywood-tours" className="hover:text-[#C9A227]">Affordable Tours</Link>
              <Link to="/hollywood-family-tours" className="hover:text-[#C9A227]">Family Tours</Link>
              <Link to="/hollywood-group-tours" className="hover:text-[#C9A227]">Group Tours</Link>
              <Link to="/hollywood-guided-tours" className="hover:text-[#C9A227]">Guided Tours</Link>
              <Link to="/hollywood-day-tour" className="hover:text-[#C9A227]">Day Tour</Link>
              <Link to="/los-angeles-sightseeing-tours" className="hover:text-[#C9A227]">LA Sightseeing</Link>
              <Link to="/hollywood-california-tours" className="hover:text-[#C9A227]">California Tours</Link>
              <Link to="/sunset-boulevard-tour" className="hover:text-[#C9A227]">Sunset Blvd Tour</Link>
              <Link to="/hollywood-music-studio-tour" className="hover:text-[#C9A227]">Music Studio Tour</Link>
              <Link to="/hollywood-travel-agency" className="hover:text-[#C9A227]">Travel Agency</Link>
              <Link to="/iata-accredited-travel-agency-hollywood" className="hover:text-[#C9A227]">IATA Accredited</Link>
              <Link to="/visit-hollywood-walk-of-fame" className="hover:text-[#C9A227]">Visit Walk of Fame</Link>
              <Link to="/hollywoodland-tours" className="hover:text-[#C9A227]">Hollywoodland Tours</Link>
              <Link to="/fifa-world-cup-2026-hollywood-tours" className="hover:text-[#C9A227] text-[#C9A227] font-semibold">FIFA 2026 Tours</Link>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Official Hollywood Tours LLC. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-[#C9A227]">Privacy Policy</a>
              <a href="#" className="hover:text-[#C9A227]">Terms of Service</a>
              <a href="#" className="hover:text-[#C9A227]">Refund Policy</a>
            </div>
          </div>
          
          {/* Kholey.com Diamond Venture Brand */}
          <div className="mt-8 pt-6 border-t border-gray-800 flex justify-center">
            <a 
              href="https://kholey.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-gray-600 hover:text-[#C9A227] transition-colors"
              title="A Kholey.com Venture"
            >
              <Gem className="w-5 h-5 group-hover:text-[#C9A227] transition-colors" />
              <span className="text-xs font-medium tracking-wider">A KHOLEY.COM VENTURE</span>
              <Gem className="w-5 h-5 group-hover:text-[#C9A227] transition-colors" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.officialhollywoodtours.com/" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
      </Helmet>
      <HeroSection />
      <FifaBanner />
      <ToursSection />
      <MusicStudioSection />
      <CustomTravelSection />
      <VegasSection />
      <BookSection />
      <FlightsSection />
      <ContactSection />
      <SeoContentSection />
    </>
  );
};

// Layout wrapper for main site pages (with Navbar + Footer)
const MainLayout = () => (
  <>
    <Navbar />
    <Home />
    <Footer />
  </>
);

// Main App
function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<MainLayout />} />
            {/* Old WordPress trip pages redirect */}
            <Route path="/voucher/:voucherNumber" element={<VoucherPage />} />
            <Route path="/admin/vouchers" element={<AdminVouchers />} />
            <Route path="/trip/*" element={<OldUrlRedirect />} />
            {/* Old WooCommerce pages redirect */}
            <Route path="/cart" element={<OldUrlRedirect />} />
            <Route path="/checkout" element={<OldUrlRedirect />} />
            <Route path="/shop" element={<OldUrlRedirect />} />
            <Route path="/my-account" element={<OldUrlRedirect />} />
            <Route path="/about-us" element={<OldUrlRedirect />} />
            <Route path="/contact-us" element={<OldUrlRedirect />} />
            <Route path="/affiliates-vendors" element={<OldUrlRedirect />} />
            <Route path="/langs" element={<OldUrlRedirect />} />
            <Route path="/book-now" element={<OldUrlRedirect />} />
            <Route path="/booking" element={<OldUrlRedirect />} />
            <Route path="/tours" element={<OldUrlRedirect />} />
            <Route path="/all-trips" element={<OldUrlRedirect />} />
            <Route path="/product/*" element={<OldUrlRedirect />} />
            <Route path="/product-category/*" element={<OldUrlRedirect />} />
            <Route path="/wp-admin/*" element={<OldUrlRedirect />} />
            <Route path="/wp-content/*" element={<OldUrlRedirect />} />
            <Route path="/wp-includes/*" element={<OldUrlRedirect />} />
            <Route path="/feed/*" element={<OldUrlRedirect />} />
            {/* New SEO landing pages */}
            <Route path="/:slug" element={<LandingPage />} />
            {/* Catch-all 404 */}
            <Route path="*" element={<OldUrlRedirect />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
